import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { bufferTime, catchError, concat, concatMap, distinctUntilChanged, EMPTY, filter, finalize, forkJoin, from, ignoreElements, map, merge, mergeMap, of, Observable, pairwise, race, switchMap, take, tap, timer, withLatestFrom } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';

import { environment } from 'src/environments/environment';
import { AppState } from 'src/app/core/app.state';
import { isMailboxSyncQueued, MailboxSyncQueueReason, mailboxSyncReasonKey, type IAccount, type IMailboxSyncResult } from '../models/account.models';
import type { IComposePanelAttachment } from '../models/compose.models';
import type { IDraftAttachment } from '../models/draft.models';
import { fileToBase64 } from '../utils/file.util';
import { DraftAttachmentUploadRegistry } from '../services/draft-attachment-upload-registry.service';
import { AccountService } from '../services/account.service';
import { ConversationService } from '../services/conversation.service';
import {
  buildConversationListParams,
  buildSentMessagesParams,
  hasNoListableMailbox,
  listableMailboxIds,
} from '../utils/conversation-params.util';
import { toListConversation } from '../utils/conversation.util';
import { DraftService } from '../services/draft.service';
import { SignatureService } from '../services/signature.service';
import { CommentService } from '../services/comment.service';
import { CommonService } from 'src/app/shared/common.service';
import { ReviewReasonPipe } from 'src/app/core/pipes/review-reason.pipe';
import { ComposeService } from '../services/compose.service';
import { InboxFolderType } from '../models/inbox.models';
import * as smartInboxActions from './smart-inbox.actions';
import type { IInboxFilter } from './smart-inbox.reducers';
import { selectAccounts, selectActiveConversation, selectActiveConversationDetails, selectActiveFolder, selectEffectiveActiveAccounts, selectFilter, selectInboxSyncState, selectIsFilterApplied } from './smart-inbox.selectors';
import { InboxService } from '../services/inbox.service';

/** Ceiling on the toast hold: a lost details response must not swallow the toast. */
const ORDER_TOAST_HOLD_TIMEOUT_MS = 10000;
/** Mock-only stand-in for the backend job duration (~2 min in real life). */
const MOCK_ORDER_CREATED_DELAY_MS = 3000;
/** Re-read the mailboxes this often while a sync is running (dropped-`idle` fallback). */
const SYNC_POLL_INTERVAL_MS = 20000;
/**
 * Ceiling on that poll. A mailbox wedged in `syncing` is cleared by the backend's stuck-sync
 * reclaim after `SYNC_STALE_MINUTES` (30); polling past that is not waiting for anything.
 */
const SYNC_POLL_MAX_TICKS = Math.ceil((35 * 60 * 1000) / SYNC_POLL_INTERVAL_MS);
/** Coalescing window for realtime upserts: one sync pass can emit one event per thread. */
const REALTIME_UPSERT_WINDOW_MS = 500;
/** Above this, the batch is a sync filling the mailbox — its threads are on page 1. */
const REALTIME_UPSERT_BURST_THRESHOLD = 5;
/** Hard cap on by-id fetches per batch. */
const REALTIME_UPSERT_BY_ID_MAX = 5;

@Injectable()
export class SmartInboxEffects {
  /** Pure formatter, same instantiation as `main-page` used before this moved here. */
  private readonly reviewReason = new ReviewReasonPipe();

  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private accountService: AccountService,
    private inboxService: InboxService,
    private conversationService: ConversationService,
    private commentService: CommentService,
    private draftService: DraftService,
    private signatureService: SignatureService,
    private draftAttachmentRegistry: DraftAttachmentUploadRegistry,
    private commonService: CommonService,
    private composeService: ComposeService,
    private translate: TranslateService,
  ) {}

  // ─── Active Folder ──────────────────────────────────────────────────────────

  setActiveFolder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(smartInboxActions.SetActiveFolder),
      withLatestFrom(this.store.select(selectFilter)),
      mergeMap(([{ folder }, currentFilter]) => {
        const resetFilter: any = {
          activeAccounts: currentFilter.activeAccounts,
        };
        return [smartInboxActions.SetFilter({ filter: resetFilter })];
      })
    )
  );

  // ─── Load Accounts ──────────────────────────────────────────────────────────

  loadAccounts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(smartInboxActions.LoadAccounts),
      switchMap(() =>
        this.accountService.listAllAccounts().pipe(
          map((res) => smartInboxActions.LoadAccountsSuccess({ accounts: res })),
          catchError(() => of(smartInboxActions.LoadAccountsError()))
        )
      )
    )
  );

  // ─── Add New Account (Connection Wizard) ────────────────────────────────────

  addNewAccount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(smartInboxActions.AddNewAccount),
      withLatestFrom(this.store.select(selectAccounts)),
      switchMap(([_, currentAccounts]) =>
        this.accountService.openConnectionWizard().pipe(
          switchMap((success) => {
            if (!success) {
              this.commonService.showToast(
                this.translate.instant('smart_inbox.toasts.failed_add_account'),
                'ERROR',
                { duration: 5000, verticalPosition: 'bottom', horizontalPosition: 'right' }
              );
              return of(smartInboxActions.AddNewAccountError());
            }

            return this.accountService.listAllAccounts().pipe(
              map((res) => {
                const newAccounts = res.data.filter(
                  (inbox) => !currentAccounts.some((a) => a.id === inbox.id)
                );
                return smartInboxActions.AddNewAccountSuccess({ accounts: newAccounts });
              }),
              catchError(() => of(smartInboxActions.AddNewAccountError()))
            );
          }),
          catchError(() => of(smartInboxActions.AddNewAccountError()))
        )
      )
    )
  );

  addNewAccountSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(smartInboxActions.AddNewAccountSuccess),
      tap(() => {
        this.commonService.showToast(this.translate.instant('smart_inbox.toasts.new_account_added'), 'INFO', {
          duration: 5000,
          verticalPosition: 'bottom',
          horizontalPosition: 'right',
        });
      }),
      withLatestFrom(this.store.select(selectActiveFolder), this.store.select(selectFilter)),
      switchMap(([{ accounts }, activeFolder, filter]) => {
        // No `RefreshAccounts` here any more: the backend enqueues the initial
        // sync inside the OAuth callback itself, so this only duplicated it —
        // and it did so through the bulk `/mailboxes/sync` route, making the
        // user wait out a second Unipile round-trip. Re-read the mailboxes
        // instead; the list banner then shows the real `sync_status`.
        return [
          smartInboxActions.LoadAccounts(),
          this.reloadListAction(activeFolder, filter, accounts),
        ];
      })
    )
  );

  // ─── Update Account ─────────────────────────────────────────────────────────

  updateAccount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(smartInboxActions.UpdateAccount),
      switchMap(({ account, payload }) =>
        this.accountService.updateAccount(account.id, payload).pipe(
          map(({ object }) =>
            smartInboxActions.UpdateAccountSuccess({ account: object })
          ),
          catchError(() => of(smartInboxActions.UpdateAccountError()))
        )
      )
    )
  );

  updateAccountSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(smartInboxActions.UpdateAccountSuccess),
        tap(() => {
          this.commonService.showToast(this.translate.instant('smart_inbox.toasts.account_updated'), 'INFO', {
            duration: 5000,
            verticalPosition: 'bottom',
            horizontalPosition: 'right',
          });
        })
      ),
    { dispatch: false }
  );

  // ─── Refresh Account ────────────────────────────────────────────────────────

  refreshAccounts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(smartInboxActions.RefreshAccounts),
      switchMap(({ accountsIds }) =>
        this.accountService.refreshAccountsConnection(accountsIds).pipe(
          map(({ data }) => smartInboxActions.RefreshAccountsSuccess({
            accountsIds,
            results: Array.isArray(data) ? data : [],
          })),
          catchError((err: HttpErrorResponse) => {
            // Used to be swallowed into a flag nothing renders, which is how a
            // 500 on this route hid in prod for two months.
            console.error('[Inbox] Mailbox sync request failed:', err);
            // 503 is the route's "nothing at all could be queued" answer — a busy server,
            // not a broken one. The per-mailbox reasons are not in the error body
            // (`error_response` carries no `data`), so the message has to stand alone.
            const key = err.status === 503
              ? 'smart_inbox.toasts.sync_all_rejected'
              : 'smart_inbox.toasts.sync_request_failed';
            this.commonService.showToast(
              this.translate.instant(key),
              'ERROR',
              { duration: 8000, verticalPosition: 'bottom', horizontalPosition: 'right' },
            );
            return of(smartInboxActions.RefreshAccountsError());
          })
        )
      )
    )
  );

  refreshAccountsSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(smartInboxActions.RefreshAccountsSuccess),
      withLatestFrom(this.store.select(selectAccounts)),
      map(([{ results }, accounts]) => {
        this.reportUnqueuedMailboxSyncs(results, accounts);

        // No list reload: 202 means queued, so the threads don't exist yet. The list fills
        // from `thread_updated` and is reloaded once by `mailboxSyncFinished$`.
        // `LoadAccounts` is the SSE-independent path to the banner — the route claims each
        // queued mailbox synchronously, so a re-read shows SYNCING even with SSE down.
        return smartInboxActions.LoadAccounts();
      })
    )
  );

  /**
   * Reload the list when a sync ends: `thread_updated` fires only for CREATED threads, so
   * updated ones and everything a backfill wrote are invisible until then.
   *
   * Driven off the derived flag, not off `MailboxSyncStatusChanged`: one edge covers a
   * whole batch, an `idle` for an already-idle mailbox (the cron's reclaim) does nothing,
   * and the poll below reuses it when SSE never delivered. `syncing → error` reloads too —
   * a failed pass usually ingested something first.
   */
  mailboxSyncFinished$ = createEffect(() =>
    this.store.select(selectInboxSyncState).pipe(
      map((syncState) => syncState.status === 'syncing'),
      distinctUntilChanged(),
      // Skips the first emission, so a page load mid-sync doesn't reload.
      pairwise(),
      filter(([wasSyncing, isSyncing]) => wasSyncing && !isSyncing),
      withLatestFrom(
        this.store.select(selectActiveFolder),
        this.store.select(selectFilter),
        this.store.select(selectEffectiveActiveAccounts),
      ),
      switchMap(([, activeFolder, filterState, activeAccounts]) => {
        const realAccounts = activeAccounts.filter((a) => a.id !== -1);
        if (!realAccounts.length) return EMPTY;

        // LoadAccounts too: the event carries no `last_sync_at` or sidebar counters.
        return of(
          smartInboxActions.LoadAccounts(),
          this.reloadListAction(activeFolder, filterState, activeAccounts),
        );
      })
    )
  );

  /**
   * Fallback completion signal. The stream has no replay buffer and a bounded queue that
   * drops silently, and the `idle` event competes in it with the sync's own
   * `thread_updated` burst — a dropped `idle` would spin the banner forever.
   *
   * `distinctUntilChanged` on the BOOLEAN stops `LoadAccountsSuccess` from restarting the
   * timer on every tick.
   */
  pollAccountsWhileSyncing$ = createEffect(() =>
    this.store.select(selectInboxSyncState).pipe(
      map((syncState) => syncState.status === 'syncing'),
      distinctUntilChanged(),
      switchMap((isSyncing) => (isSyncing
        ? timer(SYNC_POLL_INTERVAL_MS, SYNC_POLL_INTERVAL_MS).pipe(take(SYNC_POLL_MAX_TICKS))
        : EMPTY)),
      map(() => smartInboxActions.LoadAccounts())
    )
  );

  /**
   * The list-reload action for whichever folder is on screen.
   *
   * Takes accounts, not ids, so it can drop the disconnected ones and skip the request when
   * that leaves nothing — an empty `mailbox_ids` means "every mailbox" server-side.
   */
  private reloadListAction(
    activeFolder: InboxFolderType,
    filter: IInboxFilter,
    accounts: IAccount[],
  ): Action {
    if (hasNoListableMailbox(accounts)) {
      return smartInboxActions.SkipThreadListLoad();
    }

    const mailboxIds = listableMailboxIds(accounts);
    return activeFolder === InboxFolderType.SENT
      ? smartInboxActions.LoadSentMessages({ params: buildSentMessagesParams(filter, mailboxIds) })
      : smartInboxActions.LoadConversations({
          folder: activeFolder,
          params: buildConversationListParams(filter, mailboxIds),
        });
  }

  /** Reasons that are neither an error nor the user's problem. */
  private static readonly SILENT_QUEUE_REASONS: ReadonlySet<string> = new Set([
    MailboxSyncQueueReason.ALREADY_SYNCING,
  ]);

  /**
   * Tells the user which mailboxes were not queued, and why. One toast each: collapsing
   * them would hide which mailbox is broken.
   *
   * `already_syncing` is silent — the user got what they asked for and the banner says so;
   * a red toast would train them to read the banner as a failure.
   */
  private reportUnqueuedMailboxSyncs(results: IMailboxSyncResult[], accounts: IAccount[]): void {
    results
      .filter((result) => !isMailboxSyncQueued(result))
      .filter((result) => !SmartInboxEffects.SILENT_QUEUE_REASONS.has(result.reason ?? ''))
      .forEach((result) => {
        const account = accounts.find((a) => a.id === result.mailbox_id);
        const mailbox = account?.email || account?.name || `#${result.mailbox_id}`;
        const reason = this.translate.instant(mailboxSyncReasonKey(result));
        this.commonService.showToast(
          this.translate.instant('smart_inbox.toasts.mailbox_sync_not_queued', { mailbox, reason }),
          'ERROR',
          { duration: 8000, verticalPosition: 'bottom', horizontalPosition: 'right' },
        );
      });
  }

  // ─── Disconnect Account (reversible) ────────────────────────────────────────

  disconnectAccount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(smartInboxActions.DisconnectAccount),
      switchMap(({ account }) =>
        this.accountService.disconnectAccount(account.id).pipe(
          // The account stays listed, so let the server own its counters and status.
          switchMap(() => [
            smartInboxActions.DisconnectAccountSuccess({ accountId: account.id }),
            smartInboxActions.LoadAccounts(),
          ]),
          catchError(() => of(smartInboxActions.DisconnectAccountError()))
        )
      )
    )
  );

  disconnectAccountSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(smartInboxActions.DisconnectAccountSuccess),
        tap(() => {
          this.commonService.showToast(this.translate.instant('smart_inbox.toasts.account_disconnected'), 'INFO', {
            duration: 5000,
            verticalPosition: 'bottom',
            horizontalPosition: 'right',
          });
        })
      ),
    { dispatch: false }
  );

  disconnectAccountError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(smartInboxActions.DisconnectAccountError),
        tap(() => {
          this.commonService.showToast(
            this.translate.instant('smart_inbox.toasts.failed_disconnect_account'),
            'ERROR',
            { duration: 5000, verticalPosition: 'bottom', horizontalPosition: 'right' }
          );
        })
      ),
    { dispatch: false }
  );

  // ─── Delete Account (permanent) ─────────────────────────────────────────────

  deleteAccount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(smartInboxActions.DeleteAccount),
      switchMap(({ account }) =>
        this.accountService.deleteAccount(account.id).pipe(
          map(() => smartInboxActions.DeleteAccountSuccess({ accountId: account.id })),
          catchError(() => of(smartInboxActions.DeleteAccountError()))
        )
      )
    )
  );

  deleteAccountSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(smartInboxActions.DeleteAccountSuccess),
      tap(() => {
        this.commonService.showToast(this.translate.instant('smart_inbox.toasts.account_deleted'), 'INFO', {
          duration: 5000,
          verticalPosition: 'bottom',
          horizontalPosition: 'right',
        });
      }),
      withLatestFrom(
        this.store.select(selectActiveFolder),
        this.store.select(selectFilter),
        this.store.select(selectEffectiveActiveAccounts),
      ),
      // Its threads are gone, so the list must be rebuilt (unlike a disconnect).
      switchMap(([_action, activeFolder, filter, activeAccounts]) => [
        smartInboxActions.LoadAccounts(),
        this.reloadListAction(activeFolder, filter, activeAccounts),
      ])
    )
  );

  deleteAccountError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(smartInboxActions.DeleteAccountError),
        tap(() => {
          this.commonService.showToast(
            this.translate.instant('smart_inbox.toasts.failed_delete_account'),
            'ERROR',
            { duration: 5000, verticalPosition: 'bottom', horizontalPosition: 'right' }
          );
        })
      ),
    { dispatch: false }
  );

  // ─── Re-connect Account ─────────────────────────────────────────────────────

  /**
   * Not `AddNewAccount`: it keeps only ids absent from state, and a revived mailbox keeps
   * its id — so it would toast "New account added" over an empty list reload.
   */
  reconnectAccount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(smartInboxActions.ReconnectAccount),
      switchMap(({ account }) =>
        this.accountService.openConnectionWizard().pipe(
          map((success) =>
            success
              ? smartInboxActions.ReconnectAccountSuccess({ accountId: account.id })
              : smartInboxActions.ReconnectAccountError()
          ),
          catchError(() => of(smartInboxActions.ReconnectAccountError()))
        )
      )
    )
  );

  reconnectAccountSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(smartInboxActions.ReconnectAccountSuccess),
      tap(() => {
        // Neutral on purpose: the popup allows a DIFFERENT address, which creates a new
        // mailbox rather than reviving this one. "Reconnected" would not be true then.
        this.commonService.showToast(this.translate.instant('smart_inbox.toasts.connection_updated'), 'INFO', {
          duration: 5000,
          verticalPosition: 'bottom',
          horizontalPosition: 'right',
        });
      }),
      withLatestFrom(
        this.store.select(selectActiveFolder),
        this.store.select(selectFilter),
        this.store.select(selectEffectiveActiveAccounts),
      ),
      switchMap(([_action, activeFolder, filter, activeAccounts]) => [
        smartInboxActions.LoadAccounts(),
        this.reloadListAction(activeFolder, filter, activeAccounts),
      ])
    )
  );

  reconnectAccountError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(smartInboxActions.ReconnectAccountError),
        tap(() => {
          this.commonService.showToast(
            this.translate.instant('smart_inbox.toasts.failed_reconnect_account'),
            'ERROR',
            { duration: 5000, verticalPosition: 'bottom', horizontalPosition: 'right' }
          );
        })
      ),
    { dispatch: false }
  );

  // ─── Load Folder KPIs ─────────────────────────────────────────────────────

  loadFoldersKPIs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(smartInboxActions.LoadFoldersKPIs),
      switchMap(({ accounts }) =>
        this.inboxService.getFoldersKPIs(accounts).pipe(
          map(({ data }) => smartInboxActions.LoadFoldersKPIsSuccess({ kpis: data })),
          catchError(() => of(smartInboxActions.LoadFoldersKPIsError()))
        )
      )
    )
  );
  
  // ─── Filter Changed → Load data based on active folder ──────────────────────

  filterChanged$ = createEffect(() =>
    this.actions$.pipe(
      ofType(smartInboxActions.SetFilter),
      withLatestFrom(
        this.store.select(selectEffectiveActiveAccounts),
        this.store.select(selectFilter),
        this.store.select(selectActiveFolder),
      ),
      mergeMap(([_, activeAccounts, filter, activeFolder]) => {
        // Drafts and mentions are local records: only the thread lists filter.
        const mailboxIds = activeAccounts.map((a) => a.id);

        if (activeFolder === InboxFolderType.DRAFTS) {
          return [smartInboxActions.LoadDrafts({ params: { mailbox_ids: mailboxIds, page: 1, limit: 50 } })];
        }

        if (activeFolder === InboxFolderType.MENTIONS) {
          return [smartInboxActions.LoadMentions({ params: { page: 1, limit: 50 } })];
        }

        return [this.reloadListAction(activeFolder, filter, activeAccounts)];
      })
    )
  );

  // ─── Load Conversations ─────────────────────────────────────────────────────

  loadConversations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(smartInboxActions.LoadConversations),
      switchMap(({ folder, params }) =>
        this.conversationService.getConversations(folder, params).pipe(
          map((res) =>
            smartInboxActions.LoadConversationsSuccess({ conversations: res })
          ),
          catchError(() => of(smartInboxActions.LoadConversationsError()))
        )
      )
    )
  );

  // ─── Sent Messages (Load / Load More) ───────────────────────────────────────

  loadSentMessages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(smartInboxActions.LoadSentMessages),
      switchMap(({ params }) =>
        this.conversationService.getSentMessages(params).pipe(
          map((res) => smartInboxActions.LoadSentMessagesSuccess({ messages: res })),
          catchError(() => of(smartInboxActions.LoadSentMessagesError()))
        )
      )
    )
  );

  loadMoreSentMessages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(smartInboxActions.LoadMoreSentMessages),
      switchMap(({ params }) =>
        this.conversationService.getSentMessages(params).pipe(
          map((res) => smartInboxActions.LoadMoreSentMessagesSuccess({ messages: res })),
          catchError(() => of(smartInboxActions.LoadMoreSentMessagesError()))
        )
      )
    )
  );

  // ─── Select Sent Message (open conversation, highlight the message) ─────────

  selectSentMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(smartInboxActions.SelectSentMessage),
      switchMap(({ message }) =>
        this.conversationService.getConversationDetails(message.thread_id).pipe(
          map(({ object }) =>
            smartInboxActions.LoadConversationDetailsSuccess({ details: object! })
          ),
          catchError(() => of(smartInboxActions.LoadConversationDetailsError()))
        )
      )
    )
  );

  // ─── Load More Conversations ────────────────────────────────────────────────

  loadMoreConversations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(smartInboxActions.LoadMoreConversations),
      withLatestFrom(this.store.select(selectActiveFolder)),
      switchMap(([{ params }, activeFolder]) =>
        this.conversationService.getConversations(activeFolder, params).pipe(
          map((res) =>
            smartInboxActions.LoadMoreConversationsSuccess({ conversations: res })
          ),
          catchError(() => of(smartInboxActions.LoadMoreConversationsError()))
        )
      )
    )
  );

  // ─── Select Conversation (auto-load details) ───────────────────────────────

  selectConversation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(smartInboxActions.SelectConversation),
      switchMap(({ conversation }) =>
        this.conversationService.getConversationDetails(conversation.id).pipe(
          map(({ object }) =>
            smartInboxActions.LoadConversationDetailsSuccess({ details: object! })
          ),
          catchError(() => of(smartInboxActions.LoadConversationDetailsError()))
        )
      )
    )
  );

  // ─── Load Conversation Details (standalone, for refreshing after reply) ────

  loadConversationDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(smartInboxActions.LoadConversationDetails),
      switchMap(({ conversationId }) =>
        this.conversationService.getConversationDetails(conversationId).pipe(
          map(({ object }) =>
            smartInboxActions.LoadConversationDetailsSuccess({ details: object! })
          ),
          catchError(() => of(smartInboxActions.LoadConversationDetailsError()))
        )
      )
    )
  );

  // ─── Load Conversation From URL (deep link with loading UI) ─────────────────

  loadConversationFromUrl$ = createEffect(() =>
    this.actions$.pipe(
      ofType(smartInboxActions.LoadConversationFromUrl),
      switchMap(({ conversationId }) =>
        this.conversationService.getConversationDetails(conversationId).pipe(
          map(({ object }) =>
            smartInboxActions.LoadConversationDetailsSuccess({ details: object! })
          ),
          catchError(() => of(smartInboxActions.LoadConversationDetailsError()))
        )
      )
    )
  );

  loadConversationDetailsSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(smartInboxActions.LoadConversationDetailsSuccess),
      withLatestFrom(this.store.select(selectActiveFolder)),
      map(([{ details }, activeFolder]) => {
        return smartInboxActions.PatchFolderUnreadKPIs({ folder: activeFolder });
      })
    )
  );

  // ─── Realtime Event → Upsert ───────────────────────────────────────────────

  /**
   * Upserts threads named by realtime events, one page-1 read per batch.
   *
   * Batched because a sync pass emits one `thread_updated` per created thread, and the
   * previous per-event `switchMap` turned that into one *aborted* request per event —
   * landing only the last upsert and silently dropping the rest.
   *
   * KNOWN COST: `bufferTime` arms a `setInterval` for the app's lifetime. Empty emissions
   * are filtered out, but the timer still fires in the Angular zone twice a second and
   * drags a change-detection pass with it while nothing is happening.
   *
   * Event-driven windows were tried and backed out: `buffer` closed by a `debounceTime` of
   * the source arms no idle timer, but it moves when a window closes and every coalescing
   * case in `smart-inbox-realtime.effects.spec.ts` is pinned to the fixed 500ms window.
   * Keeping `bufferTime` on a zone-free scheduler needs `NgZone` in the constructor, which
   * every spec builds positionally. Revisit behind a scheduler seam the specs can drive.
   */
  realtimeEventReceived$ = createEffect(() =>
    this.actions$.pipe(
      ofType(smartInboxActions.RealtimeEventReceived),
      bufferTime(REALTIME_UPSERT_WINDOW_MS),
      map((batch) => Array.from(new Set(batch.map((action) => action.conversationId)))),
      filter((ids) => ids.length > 0),
      withLatestFrom(
        this.store.select(selectEffectiveActiveAccounts),
        this.store.select(selectFilter),
        this.store.select(selectActiveFolder),
        this.store.select(selectIsFilterApplied),
      ),
      // `concatMap`, not `switchMap`: a later window must not cancel an earlier one's
      // upserts — that cancellation is the bug above. Not `mergeMap` either: the same
      // thread can appear in two windows, and out-of-order upserts would land the stale
      // one last. Serial is safe here because each window costs ONE page-1 read regardless
      // of its size, and the notifier above caps windows at one per
      // REALTIME_UPSERT_MAX_WINDOW_MS, so the queue cannot outrun the requests.
      concatMap(([ids, activeAccounts, currentFilter, activeFolder, isFilterApplied]) => {
        if (!activeAccounts.length) return EMPTY;

        // Through the shared builder, not by hand: this page-1 read has to ask the same
        // question the visible list asked. Hand-building it dropped `team_ids` — a thread
        // from outside the filtered teams came back in the payload and the upsert reducer
        // prepended it — and it would drop the next filter added the same way.
        const params = buildConversationListParams(currentFilter, activeAccounts.map((i) => i.id));

        return this.conversationService.getConversations(activeFolder, params).pipe(
          switchMap(({ data }) => {
            const upserts: Action[] = data
              .filter((thread) => ids.includes(thread.id))
              .map((conversation) => smartInboxActions.UpsertConversation({ conversation }));
            const missing = ids.filter((id) => !data.some((thread) => thread.id === id));

            // Fetch a thread that fell off page 1 by id rather than dropping it. Skipped
            // when a filter narrows the list (a by-id fetch cannot tell whether the thread
            // still matches it) and on a large batch, where a sync is filling the mailbox
            // and anything missing is older than page 1.
            const resolveById = !isFilterApplied
              && ids.length <= REALTIME_UPSERT_BURST_THRESHOLD
              && missing.length > 0;
            if (!resolveById) return from(upserts);

            return concat(
              from(upserts),
              from(missing.slice(0, REALTIME_UPSERT_BY_ID_MAX)).pipe(
                mergeMap((id) => this.conversationService.getConversationDetails(id).pipe(
                  map(({ object }) => object
                    ? smartInboxActions.UpsertConversation({ conversation: toListConversation(object) })
                    : null),
                  catchError(() => of(null)),
                ), 2),
              ),
            );
          }),
          catchError(() => EMPTY)
        );
      }),
      filter((action): action is ReturnType<typeof smartInboxActions.UpsertConversation> => action !== null),
    )
  );

  commentMentionReceived$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(smartInboxActions.CommentMentionReceived),
        tap(({ threadSubject, threadId }) => {
          const title = this.translate.instant('smart_inbox.toasts.comment_mention');
          const detail = threadSubject ? `${threadSubject}` : '';
          const message = detail ? `${title}: <b>${detail}</b>` : title;
          this.commonService.showToast(message, 'INFO', {
            duration: 30000,
            verticalPosition: 'bottom',
            horizontalPosition: 'right',
            redirectTo: '/smart-inbox?folder=mentions&include_thread_ids=' + threadId,
          });
        })
      ),
    { dispatch: false }
  );

  threadAssignmentReceived$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(smartInboxActions.ThreadAssignmentReceived),
        tap(({ threadIds }) => {
          const message = this.translate.instant('smart_inbox.toasts.thread_assignment', { count: threadIds.length });
          this.commonService.showToast(message, 'INFO', {
            duration: 10000,
            verticalPosition: 'bottom',
            horizontalPosition: 'right',
            redirectTo: '/smart-inbox?assigned_to=me&include_thread_ids=' + threadIds.join(','),
          });
        })
      ),
    { dispatch: false }
  );

  private showOrderCreatedToast(threadId: number, threadSubject?: string): void {
    const title = this.translate.instant('smart_inbox.toasts.order_created');
    const detail = threadSubject ? `<b>${threadSubject}</b>` : '';
    this.commonService.showToast(detail ? `${title}: ${detail}` : title, 'INFO', {
      duration: 10000,
      verticalPosition: 'bottom',
      horizontalPosition: 'right',
      redirectTo: '/smart-inbox?include_thread_ids=' + threadId,
    });
  }

  /**
   * On the conversation itself, hold the toast until the refetch lands (success
   * *or* failure — the order exists either way): toasting on the event would
   * announce a success over a placeholder still spinning. Elsewhere, toast now.
   *
   * The gate reads the active conversation, not its details: details are null
   * for the whole load window, and skipping the refetch there would leave the
   * server-derived skeleton armed with no further event to clear it.
   */
  orderCreatedReceived$ = createEffect(() =>
    this.actions$.pipe(
      ofType(smartInboxActions.OrderCreatedReceived),
      withLatestFrom(this.store.select(selectActiveConversation)),
      mergeMap(([{ threadId, threadSubject }, activeConversation]) => {
        if (activeConversation?.id !== threadId) {
          this.showOrderCreatedToast(threadId, threadSubject);
          return EMPTY;
        }
        return merge(
          // Scoped to this thread: another conversation's load must not release
          // the toast. The error action carries no payload, so it is raced with
          // a timeout rather than awaited on its own.
          race(
            this.actions$.pipe(
              ofType(smartInboxActions.LoadConversationDetailsSuccess),
              filter(({ details }) => details?.id === threadId),
            ),
            this.actions$.pipe(ofType(smartInboxActions.LoadConversationDetailsError)),
            timer(ORDER_TOAST_HOLD_TIMEOUT_MS),
          ).pipe(
            take(1),
            tap(() => this.showOrderCreatedToast(threadId, threadSubject)),
            ignoreElements(),
          ),
          of(smartInboxActions.LoadConversationDetails({ conversationId: threadId })),
        );
      })
    )
  );

  /**
   * A second click while the job runs answers 409. Nothing started, so the only
   * thing to do is say so — the placeholder is left exactly as it is.
   */
  createOrderAlreadyRunning$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(smartInboxActions.CreateOrderAlreadyRunning),
        tap(() => {
          this.commonService.showToast(
            this.translate.instant('smart_inbox.toasts.order_creation_already_running'),
            'INFO'
          );
        })
      ),
    { dispatch: false }
  );

  /**
   * The single owner of the "creation failed" toast, for both ways a creation
   * can fail: the POST itself (including the 503 queue-full, which is retryable
   * and worded differently) and the terminal `order_creation_failed` event.
   * `main-page` used to toast that event itself on top of dispatching here, so
   * one failed job produced two toasts. Clearing the placeholder is the
   * reducer's job — this only toasts.
   */
  createOrderError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(smartInboxActions.CreateOrderError),
        tap(({ conversationId, queueFull, errorCode }) => {
          if (queueFull) {
            this.commonService.showToast(
              this.translate.instant('smart_inbox.toasts.order_creation_queue_full'),
              'WARNING'
            );
            return;
          }
          const title = this.translate.instant('smart_inbox.toasts.order_creation_failed');
          if (errorCode === undefined) {
            // Rejected POST: nothing to explain, and no link needed to a thread
            // the user is already looking at.
            this.commonService.showToast(title, 'ERROR', {
              duration: 10000,
              verticalPosition: 'bottom',
              horizontalPosition: 'right',
            });
            return;
          }
          // A machine token from the shared review-reason vocabulary, so the
          // wording MTO already ships in three languages (WB-7431) explains
          // *why*; the pipe falls back to a generic sentence for a code it does
          // not know. The event's own `error` is deliberately dropped: it
          // repeats the title, which read as "Order creation failed: Order
          // creation failed".
          const detail = this.translate.instant(
            this.reviewReason.transform({ review_reason: errorCode }, 'detail')
          );
          this.commonService.showToast(detail ? `${title}: <b>${detail}</b>` : title, 'ERROR', {
            duration: 10000,
            verticalPosition: 'bottom',
            horizontalPosition: 'right',
            redirectTo: '/smart-inbox?include_thread_ids=' + conversationId,
          });
        })
      ),
    { dispatch: false }
  );

  // ─── Update Conversations ──────────────────────────────────────────────────

  updateConversations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(smartInboxActions.UpdateConversations),
      switchMap(({ conversationIds, payload }) =>
        this.conversationService.updateConversations(conversationIds, payload).pipe(
          map(({ data }) =>
            smartInboxActions.UpdateConversationsSuccess({ conversationIds, payload, updatedDetails: data })
          ),
          catchError(() => of(smartInboxActions.UpdateConversationsError()))
        )
      )
    )
  );

  // ─── Message Classification Override ───────────────────────────────────────

  updateMessageClassification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(smartInboxActions.UpdateMessageClassification),
      mergeMap(({ conversationId, messageId, payload }) =>
        this.conversationService.updateMessageClassification(messageId, payload).pipe(
          map(({ object }) =>
            smartInboxActions.UpdateMessageClassificationSuccess({ conversationId, messageId, result: object! })
          ),
          catchError(() => of(smartInboxActions.UpdateMessageClassificationError({ messageId })))
        )
      )
    )
  );

  /**
   * The thread's classification / categories / is_urgent are recomputed server-side
   * from its messages, so the only way to render the new rollup is to re-fetch.
   */
  updateMessageClassificationSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(smartInboxActions.UpdateMessageClassificationSuccess),
      tap(({ conversationId }) => {
        this.store.dispatch(smartInboxActions.LoadConversationDetails({ conversationId }));
      })
    ),
    { dispatch: false }
  );

  updateMessageClassificationError$ = createEffect(() =>
    this.actions$.pipe(
      ofType(smartInboxActions.UpdateMessageClassificationError),
      tap(() => {
        this.commonService.showToast(this.translate.instant('smart_inbox.toasts.classification_update_failed'), 'ERROR', {
          duration: 5000,
          verticalPosition: 'bottom',
          horizontalPosition: 'right',
        });
      })
    ),
    { dispatch: false }
  );

  // ─── Thread Urgency Override ───────────────────────────────────────────────

  updateThreadUrgency$ = createEffect(() =>
    this.actions$.pipe(
      ofType(smartInboxActions.UpdateThreadUrgency),
      switchMap(({ conversationId, isUrgent }) =>
        this.conversationService.updateThreadUrgency(conversationId, isUrgent).pipe(
          map(() => smartInboxActions.UpdateThreadUrgencySuccess({ conversationId, isUrgent })),
          catchError(() => of(smartInboxActions.UpdateThreadUrgencyError()))
        )
      )
    )
  );

  updateThreadUrgencyError$ = createEffect(() =>
    this.actions$.pipe(
      ofType(smartInboxActions.UpdateThreadUrgencyError),
      tap(() => {
        this.commonService.showToast(this.translate.instant('smart_inbox.toasts.urgency_update_failed'), 'ERROR', {
          duration: 5000,
          verticalPosition: 'bottom',
          horizontalPosition: 'right',
        });
      })
    ),
    { dispatch: false }
  );

  // ─── Mark Conversations Read ───────────────────────────────────────────────

  markConversationsRead$ = createEffect(() =>
    this.actions$.pipe(
      ofType(smartInboxActions.MarkConversationsRead),
      switchMap(({ conversationIds, isRead }) =>
        this.conversationService.markConversationsRead(conversationIds, isRead).pipe(
          map(() =>
            smartInboxActions.MarkConversationsReadSuccess({ conversationIds, isRead })
          ),
          catchError(() => of(smartInboxActions.MarkConversationsReadError({ isRead })))
        )
      )
    )
  );

   // ─── Conversation Update Success ───────────────────────────────────────────

   updateConversationsSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(smartInboxActions.UpdateConversationsSuccess),
      withLatestFrom(this.store.select(selectActiveConversation)),
      tap(([{ conversationIds, payload }, activeConversation]) => {
        if (activeConversation) {
          this.store.dispatch(smartInboxActions.LoadConversationDetails({ conversationId: activeConversation.id }));
        }

        // TODO: Add back the logic to load conversation details only if the active conversation is included in the updated conversation ids and the update has a customer id
        // const isActiveIncluded = activeConversation != null && conversationIds.includes(activeConversation.id);
        // const updateHasCustomerId = payload.customer_id != null;
        // if (isActiveIncluded && updateHasCustomerId) {
        //   this.store.dispatch(smartInboxActions.LoadConversationDetails({ conversationId: activeConversation!.id }));
        // }
        this.commonService.showToast(this.translate.instant('smart_inbox.toasts.conversations_updated'), 'INFO', {
          duration: 5000,
          verticalPosition: 'bottom',
          horizontalPosition: 'right',
        });
      })
    ),
    { dispatch: false }
  );

  markConversationsReadSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(smartInboxActions.MarkConversationsReadSuccess),
        tap(({ isRead }) => {
          const key = isRead ? 'smart_inbox.toasts.conversations_marked_read' : 'smart_inbox.toasts.conversations_marked_unread';
          this.commonService.showToast(this.translate.instant(key), 'INFO', {
            duration: 5000,
            verticalPosition: 'bottom',
            horizontalPosition: 'right',
          });
        })
      ),
    { dispatch: false }
  );

  // ─── Comments ────────────────────────────────────────────────────────────

  createComment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(smartInboxActions.CreateComment),
      switchMap(({ conversationId, message }) =>
        this.commentService.createComment(conversationId, message).pipe(
          map(({ object }) => smartInboxActions.CreateCommentSuccess({ conversationId, object })),
          catchError(() => of(smartInboxActions.CreateCommentError()))
        )
      )
    )
  );

  updateComment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(smartInboxActions.UpdateComment),
      switchMap(({ conversationId, commentId, body }) =>
        this.commentService.updateComment(conversationId, commentId, body).pipe(
          map(({ object }) => smartInboxActions.UpdateCommentSuccess({ conversationId, commentId: object.id, object })),
          catchError(() => of(smartInboxActions.UpdateCommentError()))
        )
      )
    )
  );

  deleteComment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(smartInboxActions.DeleteComment),
      switchMap(({ conversationId, commentId }) =>
        this.commentService.deleteComment(conversationId, commentId).pipe(
          map(() => smartInboxActions.DeleteCommentSuccess({ conversationId, commentId })),
          catchError(() => of(smartInboxActions.DeleteCommentError()))
        )
      )
    )
  );

  // ─── Send (Compose / Reply / Reply All / Forward) ─────────────────────────────────────────────────

  sendCompose$ = createEffect(() =>
    this.actions$.pipe(
      ofType(smartInboxActions.SendCompose),
      switchMap(({ panelId, replyToConversationId }) => {
        this.composeService.setSending(panelId, true);
        const draftId = this.composeService.getPanelById(panelId)?.draftId ?? null;
        return this.composeService.sendCompose(panelId).pipe(
          map(() => {
            this.composeService.closeCompose(panelId);
            return smartInboxActions.SendComposeSuccess({ panelId, replyToConversationId, draftId });
          }),
          catchError(() => {
            this.composeService.setSending(panelId, false);
            return of(smartInboxActions.SendComposeError({ panelId }));
          })
        );
      })
    )
  );

  sendComposeSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(smartInboxActions.SendComposeSuccess),
      withLatestFrom(this.store.select(selectActiveConversation)),
      tap(([{ replyToConversationId }, activeConversation]) => {
        this.commonService.showToast(this.translate.instant('smart_inbox.toasts.email_sent'), 'INFO', {
          duration: 5000,
          verticalPosition: 'bottom',
          horizontalPosition: 'right',
        });

        if (replyToConversationId && activeConversation && activeConversation.id === replyToConversationId) {
          this.store.dispatch(smartInboxActions.LoadConversationDetails({
            conversationId: replyToConversationId,
          }));
        }
      })
    ),
    { dispatch: false }
  );

  // ─── Mentions ──────────────────────────────────────────────────────────────

  loadMentions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(smartInboxActions.LoadMentions),
      switchMap(({ params }) =>
        this.commentService.listMentions(params).pipe(
          map((res) => smartInboxActions.LoadMentionsSuccess({ mentions: res })),
          catchError(() => of(smartInboxActions.LoadMentionsError()))
        )
      )
    )
  );

  loadMoreMentions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(smartInboxActions.LoadMoreMentions),
      switchMap(({ params }) =>
        this.commentService.listMentions(params).pipe(
          map((res) => smartInboxActions.LoadMoreMentionsSuccess({ mentions: res })),
          catchError(() => of(smartInboxActions.LoadMoreMentionsError()))
        )
      )
    )
  );

  selectConversationFromComment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(smartInboxActions.SelectConversationFromComment),
      switchMap(({ mention }) =>
        this.conversationService.getConversationDetails(mention.thread_id, mention.id).pipe(
          map(({ object }) =>
            smartInboxActions.LoadConversationDetailsSuccess({ details: object! })
          ),
          catchError(() => of(smartInboxActions.LoadConversationDetailsError()))
        )
      )
    )
  );

  // ─── Drafts ─────────────────────────────────────────────────────────────────

  loadDrafts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(smartInboxActions.LoadDrafts),
      switchMap(({ params }) =>
        this.draftService.listDrafts(params).pipe(
          map((res) => smartInboxActions.LoadDraftsSuccess({ drafts: res })),
          catchError(() => of(smartInboxActions.LoadDraftsError()))
        )
      )
    )
  );

  loadMoreDrafts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(smartInboxActions.LoadMoreDrafts),
      switchMap(({ params }) =>
        this.draftService.listDrafts(params).pipe(
          map((res) => smartInboxActions.LoadMoreDraftsSuccess({ drafts: res })),
          catchError(() => of(smartInboxActions.LoadMoreDraftsError()))
        )
      )
    )
  );

  createDraft$ = createEffect(() =>
    this.actions$.pipe(
      ofType(smartInboxActions.CreateDraft),
      mergeMap(({ panel }) =>
        this.draftService.createDraft(panel).pipe(
          map((res) => smartInboxActions.CreateDraftSuccess({ panelId: panel.id, draft: res.object! })),
          catchError((err) => of(smartInboxActions.CreateDraftError({ panelId: panel.id, error: err?.error?.message }))),
        )
      )
    )
  );

  createDraftSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(smartInboxActions.CreateDraftSuccess),
        tap(({ panelId, draft }) => {
          this.composeService.setDraftId(panelId, draft.id);
        }),
      ),
    { dispatch: false }
  );

  uploadDraftAttachments$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(smartInboxActions.CreateDraftSuccess, smartInboxActions.UploadDraftAttachments),
        mergeMap((action) => {
          const panelId = action.panelId;
          const panel = this.composeService.getPanelById(panelId);
          const draftId = 'draft' in action ? action.draft.id : panel?.draftId ?? null;
          if (!draftId || !panel?.attachments?.length) return EMPTY;
          const pending = panel.attachments.filter((a): a is IComposePanelAttachment & { file: File } => !a.id && !!a.file);
          if (pending.length === 0) return EMPTY;
          return forkJoin(
            pending.map((att) => {
              const controller = new AbortController();
              this.draftAttachmentRegistry.register(panelId, att, controller);
              return fileToBase64(att.file).pipe(
                switchMap((base64) =>
                  this.draftService.addDraftAttachment(draftId, att.name, base64, att.file.type || undefined, controller.signal).pipe(
                    map((res) => ({ att, server: res.object! })),
                  )
                ),
                finalize(() => this.draftAttachmentRegistry.unregister(panelId, att)),
              );
            }),
          ).pipe(
            tap((results) => {
              const currentPanel = this.composeService.getPanelById(panelId);
              const existing = currentPanel?.attachments ?? [];
              const updated: IComposePanelAttachment[] = existing.map((a) => {
                const found = results.find((r) => r.att === a);
                if (found) return { ...a, id: found.server.id, name: found.server.name, size: found.server.file_size };
                return a;
              });
              this.composeService.setAttachments(panelId, updated);
            }),
            catchError((err) => {
              const isAbort = err?.name === 'AbortError' || (err?.message && String(err.message).toLowerCase().includes('abort'));
              if (!isAbort) {
                this.commonService.showToast(this.translate.instant('smart_inbox.toasts.failed_add_attachment'), 'ERROR', {
                  duration: 5000,
                  verticalPosition: 'bottom',
                  horizontalPosition: 'right',
                });
              }
              return EMPTY;
            }),
          );
        }),
      ),
    { dispatch: false }
  );

  updateDraft$ = createEffect(() =>
    this.actions$.pipe(
      ofType(smartInboxActions.UpdateDraft),
      mergeMap(({ panel, draftId }) =>
        this.draftService.updateDraft(draftId, panel).pipe(
          map((res) => smartInboxActions.UpdateDraftSuccess({ panelId: panel.id, draft: res.object! })),
          catchError((err) => of(smartInboxActions.UpdateDraftError({ panelId: panel.id, error: err?.error?.message }))),
        )
      )
    )
  );

  deleteDraft$ = createEffect(() =>
    this.actions$.pipe(
      ofType(smartInboxActions.DeleteDraft),
      switchMap(({ draftId }) =>
        this.draftService.deleteDraft(draftId).pipe(
          map(() => smartInboxActions.DeleteDraftSuccess({ draftId })),
          catchError(() => of({ type: 'NO_OP' }))
        )
      )
    )
  );

  removeDraftAttachment$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(smartInboxActions.RemoveDraftAttachment),
        mergeMap(({ panelId, attachment }) => {
          const panel = this.composeService.getPanelById(panelId);
          if (!panel) return EMPTY;
          const current = panel.attachments ?? [];
          const next = current.filter((a) =>
            attachment.id != null ? a.id !== attachment.id : a !== attachment
          );
          if (attachment.id == null) {
            this.draftAttachmentRegistry.abortAndRemove(panelId, attachment);
          }
          if (attachment.id != null && panel.draftId != null) {
            return this.draftService.removeDraftAttachment(panel.draftId!, attachment.id).pipe(
              tap(() => {
                this.composeService.setAttachments(panelId, next);
              }),
              catchError(() => EMPTY),
            );
          }
          this.composeService.setAttachments(panelId, next);
          return EMPTY;
        }),
      ),
    { dispatch: false }
  );

  // ─── Create Order ──────────────────────────────────────────────────────────

  // The API answers 202: matching runs in the background (~2 min). The
  // placeholder added by the CreateOrder reducer stays up until the
  // order_created / order_creation_failed realtime event resolves it.
  createOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(smartInboxActions.CreateOrder),
      switchMap(({ conversationId }) =>
        this.conversationService.createOrder(conversationId).pipe(
          switchMap(() =>
            // Mock mode has no SSE channel, so nothing would ever emit
            // order_created and the placeholder would spin forever on a
            // documented dev path: simulate the completion on a timer.
            environment.useMockData
              ? concat(
                  of(smartInboxActions.CreateOrderAccepted({ conversationId })),
                  timer(MOCK_ORDER_CREATED_DELAY_MS).pipe(
                    map(() => smartInboxActions.OrderCreatedReceived({ threadId: conversationId })),
                  ),
                )
              : of(smartInboxActions.CreateOrderAccepted({ conversationId })),
          ),
          catchError((error: HttpErrorResponse) => {
            // 409: a creation is already running for this thread. Nothing new
            // started, so the placeholder already on screen must stay up —
            // hence a dedicated action rather than CreateOrderError, which the
            // reducer uses to clear it.
            if (error?.status === 409) {
              return of(smartInboxActions.CreateOrderAlreadyRunning({ conversationId }));
            }
            // 503: the queue was full, nothing started — retrying is safe.
            return of(
              smartInboxActions.CreateOrderError({ conversationId, queueFull: error?.status === 503 })
            );
          })
        )
      )
    )
  );

  // ─── AI Draft ───────────────────────────────────────────────────────────

  generateAiDraft$ = createEffect(() =>
    this.actions$.pipe(
      ofType(smartInboxActions.GenerateAiDraft),
      switchMap(({ panelId, conversationId }) =>
        this.draftService.generateAiDraft(conversationId).pipe(
          map((res) => {
            this.composeService.updatePanelBody(panelId, res.object!.body);
            this.composeService.saveDraft(panelId);
            return smartInboxActions.GenerateAiDraftSuccess({
              panelId,
              body: res.object!.body,
              placeholders: res.object!.placeholders ?? [],
              reasoning: (res.object as any).reasoning,
              processingStatus: (res.object as any).processing_status,
              processingErrors: (res.object as any).processing_errors,
            });
          }),
          catchError(() => of(smartInboxActions.GenerateAiDraftError({ panelId })))
        )
      )
    )
  );

  // ─── Signatures ────────────────────────────────────────────────────────────

  loadSignatures$ = createEffect(() =>
    this.actions$.pipe(
      ofType(smartInboxActions.LoadSignatures),
      switchMap(({ mailboxId }) =>
        this.signatureService.list(mailboxId).pipe(
          map(({ data }) => smartInboxActions.LoadSignaturesSuccess({ signatures: data })),
          catchError(() => of(smartInboxActions.LoadSignaturesError()))
        )
      )
    )
  );

  createSignature$ = createEffect(() =>
    this.actions$.pipe(
      ofType(smartInboxActions.CreateSignature),
      switchMap(({ mailboxId, payload }) =>
        this.signatureService.create(mailboxId, payload).pipe(
          map(({ object }) => smartInboxActions.CreateSignatureSuccess({ mailboxId, signature: object })),
          catchError(() => of(smartInboxActions.CreateSignatureError()))
        )
      )
    )
  );

  createSignatureSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(smartInboxActions.CreateSignatureSuccess),
      tap(() => {
        this.commonService.showToast(this.translate.instant('smart_inbox.toasts.signature_created'), 'INFO', {
          duration: 5000,
          verticalPosition: 'bottom',
          horizontalPosition: 'right',
        });
      }),
      switchMap(({ mailboxId }) => [
        smartInboxActions.LoadSignatures({ mailboxId }),
        smartInboxActions.LoadAccounts(),
      ])
    )
  );

  updateSignature$ = createEffect(() =>
    this.actions$.pipe(
      ofType(smartInboxActions.UpdateSignature),
      switchMap(({ mailboxId, signatureId, payload }) =>
        this.signatureService.update(mailboxId, signatureId, payload).pipe(
          map(({ object }) => smartInboxActions.UpdateSignatureSuccess({ mailboxId, signature: object })),
          catchError(() => of(smartInboxActions.UpdateSignatureError()))
        )
      )
    )
  );

  updateSignatureSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(smartInboxActions.UpdateSignatureSuccess),
      tap(() => {
        this.commonService.showToast(this.translate.instant('smart_inbox.toasts.signature_updated'), 'INFO', {
          duration: 5000,
          verticalPosition: 'bottom',
          horizontalPosition: 'right',
        });
      }),
      switchMap(({ mailboxId }) => [
        smartInboxActions.LoadSignatures({ mailboxId }),
        smartInboxActions.LoadAccounts(),
      ])
    )
  );

  deleteSignature$ = createEffect(() =>
    this.actions$.pipe(
      ofType(smartInboxActions.DeleteSignature),
      switchMap(({ mailboxId, signatureId }) =>
        this.signatureService.delete(mailboxId, signatureId).pipe(
          map(() => smartInboxActions.DeleteSignatureSuccess({ mailboxId, signatureId })),
          catchError(() => of(smartInboxActions.DeleteSignatureError()))
        )
      )
    )
  );

  deleteSignatureSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(smartInboxActions.DeleteSignatureSuccess),
      tap(() => {
        this.commonService.showToast(this.translate.instant('smart_inbox.toasts.signature_deleted'), 'INFO', {
          duration: 5000,
          verticalPosition: 'bottom',
          horizontalPosition: 'right',
        });
      }),
      switchMap(({ mailboxId }) => [
        smartInboxActions.LoadSignatures({ mailboxId }),
        smartInboxActions.LoadAccounts(),
      ])
    )
  );

}
